import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Heart, Share2 } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api } from "../api";

export default function TipSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", txn: null });
  const celebrated = useRef(false);

  useEffect(() => {
    if (!sessionId) return setState({ status: "missing" });
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") setState({ status: "paid", txn: r.data });
        else if (attempts >= 8) setState({ status: "timeout", txn: r.data });
        else setTimeout(poll, 2000);
      } catch { setState({ status: "error" }); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Brutalist confetti burst — orange & white shards from both lower corners.
  useEffect(() => {
    if (state.status !== "paid" || celebrated.current) return;
    celebrated.current = true;
    const colors = ["#FF4500", "#ffffff", "#FFD23F"];
    const burst = (origin) =>
      confetti({
        particleCount: 80,
        spread: 70,
        startVelocity: 45,
        gravity: 0.9,
        ticks: 200,
        colors,
        origin,
        shapes: ["square"],
        scalar: 0.9,
      });
    burst({ x: 0.2, y: 0.8 });
    burst({ x: 0.8, y: 0.8 });
    setTimeout(() => burst({ x: 0.5, y: 0.4 }), 250);

    // Receipt-style toast with a "Share" action button that copies the artist URL
    const amt = state.txn?.amount?.toFixed?.(2);
    const artistName = state.txn?.metadata?.artist_name || state.txn?.artist_name;
    const artistUsername = state.txn?.metadata?.artist_username || state.txn?.artist_username;
    if (amt) {
      toast.success(`Tip sent · $${amt}${artistName ? ` to ${artistName}` : ""}`, {
        duration: 6000,
        action: artistUsername
          ? {
              label: "SHARE",
              onClick: () => {
                const url = `${window.location.origin}/artist/${artistUsername}`;
                if (navigator.share) navigator.share({ title: artistName, url }).catch(() => {});
                else navigator.clipboard.writeText(url).then(() => toast.info("Link copied"));
              },
            }
          : undefined,
      });
    }
  }, [state]);

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-20 text-center pb-32" data-testid="tip-success-page">
      {state.status === "loading" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[var(--accent)]" size={42} />
          <h1 className="font-display text-4xl uppercase mt-6" data-testid="tip-status-loading">Processing your tip…</h1>
          <p className="font-mono-label text-[10px] text-neutral-500 mt-3">Don&apos;t close this tab.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div className="relative inline-block mb-4">
            <CheckCircle2 className="mx-auto text-[var(--accent)] animate-pulse" size={64} />
            <Heart className="absolute -top-1 -right-2 text-[var(--accent)] fill-[var(--accent)]" size={20} />
          </div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2" data-testid="tip-status-paid-pill">TRANSACTION CONFIRMED</div>
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none">Tip Sent</h1>
          <p className="text-neutral-300 mt-5 text-lg">
            Thanks for supporting the culture. <span className="text-[var(--accent)] font-headline">${state.txn?.amount?.toFixed?.(2)}</span> delivered.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/" className="btn-primary" data-testid="tip-back-home">Back Home</Link>
            <button
              onClick={() => {
                const url = window.location.origin + "/";
                if (navigator.share) navigator.share({ title: "Hiphopistheculture", url }).catch(() => {});
                else navigator.clipboard.writeText(url).then(() => toast.success("Link copied to clipboard"));
              }}
              className="btn-ghost"
              data-testid="tip-share-platform"
            >
              <Share2 size={14} /> Spread the Word
            </button>
          </div>
        </>
      )}
      {(state.status === "error" || state.status === "timeout" || state.status === "missing") && (
        <>
          <AlertTriangle className="mx-auto text-[var(--accent)]" size={42} />
          <h1 className="font-display text-4xl uppercase mt-6" data-testid="tip-status-error">Could not confirm tip</h1>
          <p className="text-neutral-400 mt-3">If Stripe charged your card, the payment is safe — refresh in a minute or reach out to support.</p>
          <Link to="/" className="btn-ghost mt-6">Home</Link>
        </>
      )}
    </div>
  );
}
